import type {
  BookingHit,
  PrivateEventHit,
  SearchResultGroup,
  UpcomingWorkshopHit,
  VenueHit,
} from '../../../api/search';
import { formatVenueDate } from '../../../utils/venueTime';
import type { PaletteCommand, PaletteSection } from '../commandPalette.types';

import type { RecentSearch } from './recent-searches';
import { SEARCH_TARGETS, type SearchTargetKind } from './search-kinds';

const WHEN_PATTERN = 'EEE, MMM d · h:mm a';

function when(localStartsAt: string | null): string {
  return formatVenueDate(localStartsAt, WHEN_PATTERN, '');
}

/** Joins the parts that exist, so a missing field leaves no stray separator. */
function joinParts(...parts: (string | null | undefined)[]): string {
  return parts.filter(Boolean).join(' · ');
}

function targetRow(
  kind: SearchTargetKind,
  id: string,
  label: string,
  detail: string,
  extra: Partial<PaletteCommand> = {},
): PaletteCommand {
  const target = SEARCH_TARGETS[kind];
  return {
    id: `${kind}-${id}`,
    label,
    detail: detail || undefined,
    icon: target.icon,
    hint: target.label,
    to: target.href(id),
    recent: { kind, id, label, detail: detail || null },
    ...extra,
  };
}

function seatsText(seatsLeft: number): string {
  if (seatsLeft === 0) return 'Full';
  return seatsLeft === 1 ? '1 seat left' : `${seatsLeft} seats left`;
}

function workshopRow(workshop: UpcomingWorkshopHit): PaletteCommand {
  return targetRow(
    'workshop',
    workshop.id,
    workshop.name ?? 'Workshop',
    joinParts(when(workshop.localStartsAt), seatsText(workshop.seatsLeft)),
    { indent: true },
  );
}

function venueRows(venue: VenueHit): PaletteCommand[] {
  const place = joinParts(venue.city, venue.state);
  const detail = venue.isActive ? place : joinParts(place, 'Inactive');
  return [
    targetRow('venue', venue.id, venue.name, detail),
    ...venue.upcomingWorkshops.map(workshopRow),
  ];
}

function bookingRow(booking: BookingHit): PaletteCommand {
  const label = joinParts(booking.displayRef ?? 'Booking', booking.customerName);
  const detail = joinParts(booking.workshopName, booking.venueName, when(booking.localStartsAt));
  return targetRow('booking', booking.id, label, detail);
}

function privateEventRow(event: PrivateEventHit): PaletteCommand {
  const detail = joinParts(event.contactName, event.locationName, when(event.localStartsAt));
  return targetRow('privateEvent', event.id, event.clientName, detail);
}

function rowsFor(group: SearchResultGroup): PaletteCommand[] {
  switch (group.kind) {
    case 'venue':
      return group.items.flatMap(venueRows);
    case 'booking':
      return group.items.map(bookingRow);
    case 'privateEvent':
      return group.items.map(privateEventRow);
  }
}

/** One section per group that found something. Empty groups are left out. */
export function resultSections(groups: SearchResultGroup[]): PaletteSection[] {
  return groups
    .filter((group) => group.items.length > 0)
    .map((group) => ({
      id: group.kind,
      title: SEARCH_TARGETS[group.kind].groupLabel,
      count:
        group.total > group.items.length ? `${group.items.length} of ${group.total}` : undefined,
      rows: rowsFor(group),
    }));
}

export function recentRows(recent: RecentSearch[]): PaletteCommand[] {
  return recent.map((item) => ({
    ...targetRow(item.kind, item.id, item.label, item.detail ?? ''),
    id: `recent-${item.kind}-${item.id}`,
  }));
}
